import type {
    PromozavrQueryParams,
    SelectPromoModelInput,
    SelectPromoModelServerInput,
    SelectPromoSuppliersData,
} from '../types/promozavr';
import { FETCH_YOURSELF, PROMOZAVR_PROJECT, PROMOZAVR_STAGE, PROMOZAVR_TEST_APP } from '../types/promozavr';

type BuildSelectPromoModelInputArgs = PromozavrQueryParams & {
    lang?: string;
    tld?: string;
    testIds?: string[];
};

type BuildSsrSelectPromoModelInputArgs = BuildSelectPromoModelInputArgs & {
    fastFail?: boolean;
};

type ShouldInitPromozavrArgs = {
    isPaasEnabled: boolean;
    pathname: string;
    isMessenger: boolean;
    isMeetingRoom: boolean;
};

const PROMOZAVR_ALLOWED_PATHS = new Set([
    '/',
    '/connect-to-meeting-by-id',
    '/planning',
    '/broadcast-planning',
    '/create-broadcast',
]);

function splitPromozavrQueryParam(rawParams: string) {
    const params: string[] = [];
    let lastSeparatorIndex = 0;
    let squareBracketDepth = 0;

    for (let index = 0; index < rawParams.length; index += 1) {
        const symbol = rawParams[index];

        if (symbol === '[') {
            squareBracketDepth += 1;
            continue;
        }

        if (symbol === ']') {
            squareBracketDepth = Math.max(squareBracketDepth - 1, 0);
            continue;
        }

        if (symbol === ',' && squareBracketDepth === 0) {
            const param = rawParams.slice(lastSeparatorIndex, index);

            if (param) {
                params.push(param);
            }

            lastSeparatorIndex = index + 1;
        }
    }

    const lastParam = rawParams.slice(lastSeparatorIndex);

    if (lastParam) {
        params.push(lastParam);
    }

    return params;
}

function parsePromozavrRawParams(rawParams: string): PromozavrQueryParams {
    const params: PromozavrQueryParams = {};
    const debugParams: string[] = [];

    splitPromozavrQueryParam(rawParams).forEach((param) => {
        const separatorIndex = param.indexOf(':');

        if (separatorIndex <= 0) {
            debugParams.push(param);

            return;
        }

        const key = param.slice(0, separatorIndex);
        const value = param.slice(separatorIndex + 1);

        if (key === 'app') {
            if (value === PROMOZAVR_TEST_APP) {
                params.app = value;

                return;
            }

            return;
        }

        if (key === 'debug') {
            if (value) {
                params.debug = value;

                return;
            }
        }

        debugParams.push(param);
    });

    if (debugParams.length > 0) {
        params.debugParams = debugParams.join(',');
    }

    return params;
}

function normalizePathname(pathname: string) {
    if (!pathname || pathname === '/') {
        return '/';
    }

    return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

export function getPromozavrTestIdsFromExperimentBoxes(boxes: string[] = []) {
    return boxes
        .map((box) => {
            const separatorIndex = box.indexOf(',');

            return separatorIndex >= 0 ? box.slice(0, separatorIndex) : box;
        })
        .filter(Boolean);
}

export function createPromozavrSuppliersData(): SelectPromoSuppliersData {
    return {
        bunkerConfigSupplierData: FETCH_YOURSELF,
        billingDataSupplierData: FETCH_YOURSELF,
        billingSubscriptionsDataSupplierData: FETCH_YOURSELF,
        blackboxUserInfoSupplierData: FETCH_YOURSELF,
        datasyncPromoDataSupplierData: FETCH_YOURSELF,
        datasyncUserFeaturesDataSupplierData: FETCH_YOURSELF,
        saasKvSegmentsSupplierData: FETCH_YOURSELF,
    };
}

export function buildSelectPromoModelInput({
    lang,
    tld,
    testIds = [],
    app,
    debug,
    debugParams,
}: BuildSelectPromoModelInputArgs): SelectPromoModelInput {
    const request: SelectPromoModelInput = {
        project: PROMOZAVR_PROJECT,
        stage: PROMOZAVR_STAGE,
        lang,
        tld,
        testIds,
        skipPromos: [],
        selectAllPromos: false,
        usePersistentQueues: false,
        suppliersData: createPromozavrSuppliersData(),
    };

    if (app) {
        request.app = app;
    }

    if (debug) {
        request.debug = debug;
    }

    if (debugParams) {
        request.debugParams = debugParams;
    }

    return request;
}

export function buildSsrSelectPromoModelInput({
    fastFail,
    ...args
}: BuildSsrSelectPromoModelInputArgs): SelectPromoModelServerInput {
    const request: SelectPromoModelServerInput = buildSelectPromoModelInput(args);

    if (fastFail) {
        request.fastFail = true;
    }

    return request;
}

export function parsePromozavrQueryParams(search: string): PromozavrQueryParams {
    const rawParams = new URLSearchParams(search).get('promozavr');

    if (!rawParams) {
        return {};
    }

    return parsePromozavrRawParams(rawParams);
}

export function getPromozavrDebugParams(search: string, isYandexNet = false) {
    if (!isYandexNet) {
        return undefined;
    }

    return parsePromozavrQueryParams(search).debugParams;
}

export function isPromozavrRouteAllowed(pathname: string) {
    return PROMOZAVR_ALLOWED_PATHS.has(normalizePathname(pathname));
}

export function getPromozavrRequestKey(pathname: string, search: string) {
    return `${normalizePathname(pathname)}${search}`;
}

export function shouldInitPromozavr({ isPaasEnabled, pathname, isMessenger, isMeetingRoom }: ShouldInitPromozavrArgs) {
    return isPaasEnabled && isPromozavrRouteAllowed(pathname) && !isMessenger && !isMeetingRoom;
}
